import type { ClientFlowAction } from '~/graphql/types.client';
import getActionsOnLowestLevel from '../getActionsOnLowestLevel';

/**
 * Returns the deepest leaf action in a branch.
 * If there are multiple actions on the same level, it returns the first one.
 *
 * @param {Array<ClientFlowAction>} branch - The branch to search in
 * @returns {ClientFlowAction | null} The deepest leaf action or null if the branch is empty
 */
const getDeepestLeaf = (
  branch: Array<ClientFlowAction>,
): ClientFlowAction | null => {
  if (branch.length === 0) return null;

  // Fast membership check to ignore parent references that point outside this branch.
  const ids = new Set(branch.map(a => a.id));

  // Assign each action a DAG depth (distance from the nearest root in this branch).
  // We receive a topologically sorted branch from resetAndCloneBranch,
  // so parents are always processed before their children — one pass is enough.
  const depthById = new Map<string, number>();
  for (const action of branch) {
    let depth = 0;
    if (action.__typename !== 'FlowV2_Action_Start' && 'parentIds' in action) {
      for (const parentId of action.parentIds) {
        // Skip parents that belong to a different branch (outside the copied subset).
        if (!ids.has(parentId)) continue;
        // depth = longest path from any root to this action.
        const parentDepth = depthById.get(parentId) ?? 0;
        depth = Math.max(depth, parentDepth + 1);
      }
    }
    depthById.set(action.id, depth);
  }

  // Leaf nodes = actions that have no children within this branch.
  const lowestLevel = getActionsOnLowestLevel(branch);
  if (lowestLevel.length === 0) return null;

  // Among all leaves, pick the one furthest from the root.
  // In a linear chain there is only one leaf; in a branching flow there may be
  // several and we want the one that sits deepest in the DAG.
  let best: ClientFlowAction | null = null;
  let bestDepth = -1;
  for (const leaf of lowestLevel) {
    const d = depthById.get(leaf.id) ?? 0;
    if (d > bestDepth) {
      bestDepth = d;
      best = leaf;
    }
  }

  return best;
};
export default getDeepestLeaf;
